import { Card as MuiCard, CardProps as MuiCardProps, styled } from "@mui/material";

import { Box } from "@/Components/v2/atoms/Box";
import { Tag } from "@/Components/v2/atoms/Tag";
import { Typography } from "@/Components/v2/atoms/Typography";
import { TestingRecommendationTag } from "@/pages/growth-center/api/getTestingRecommendations";

import { Tooltip } from "../ToolTip";

interface ExperimentCardProps extends MuiCardProps {
  title: string;
  description: string;
  tags?: TestingRecommendationTag[];
  isSelected?: boolean;
  onClick?: () => void;
}

const StyledCard = styled(MuiCard, {
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "isHovered",
})<{ isSelected?: boolean; isHovered?: boolean }>(({ theme, isSelected }) => {
  const getBackgroundColor = () => {
    if (isSelected) {
      return theme.palette.mode === "dark" ? "#2B3033" : "#F6F9FD";
    }
    return "none"; // White for non-selected state
  };

  const getOutlineColor = () => {
    return theme.palette.mode === "dark" ? "#656565" : "#e0e0e0"; // Gray outline for inactive cards
  };

  return {
    // padding: "1.5rem",
    borderRadius: "0.5rem",
    backgroundColor: getBackgroundColor(),
    backgroundImage: "none",
    border: "none",
    outline: isSelected ? "none" : `1px solid ${getOutlineColor()}`, // Gray outline for inactive cards
    outlineOffset: isSelected ? "0" : "-1px", // Inset outline for inactive cards
    boxShadow: isSelected ? `inset 2px 0 0 0 #e91e63` : "none", // Pink left border for selected only
    cursor: "pointer",
    width: "100%",
    minHeight: "180px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    transition: "background-color 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: !isSelected
        ? theme.palette.mode === "dark"
          ? "#2B3033"
          : "#F6F9FD"
        : undefined,
    },
  };
});

export const ExperimentCard: React.FC<ExperimentCardProps> = ({
  title,
  description,
  tags,
  isSelected = false,
  onClick,
  ...props
}) => {
  const textColor = isSelected ? "white" : "#333333";
  const descriptionColor = isSelected ? "white" : "#666666";

  return (
    <StyledCard isSelected={isSelected} onClick={onClick} {...props}>
      <Box p={2}>
        {tags && tags.length > 0 && (
          <Box mb={2} display="flex" gap={1}>
            {tags.map((tag) => (
              <Tooltip key={tag.id} title={tag.tooltip || tag.name} arrow placement="top">
                <span>
                  <Tag label={tag.name} color={tag.color} />
                </span>
              </Tooltip>
            ))}
          </Box>
        )}
        <Typography variant="h6" component="h3" fontWeight={600} mb={1} color={textColor}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          color={descriptionColor}
          sx={{ lineHeight: 1.5, opacity: isSelected ? 0.8 : 1 }}
        >
          {description}
        </Typography>
      </Box>
    </StyledCard>
  );
};

export default ExperimentCard;
